<script lang="ts">
  import { createMutation } from "@tanstack/svelte-query";
  import {
    formatPhoneNumber,
    hasLetters,
    formatUSPhoneNumber,
    textToNumbers,
  } from "@/lib/helpers/phone";
  import { IconArrowNarrowRight } from "@tabler/icons-svelte";

  import cn from "classnames";

  import TextInput from "@/lib/components/text-input/text-input.svelte";
  import PhoneInput from "@/lib/components/phone-input/phone-input.svelte";

  import { searchPhoneNumber } from "@/lib/api/numbers-search";
  import type { Payload } from "@/lib/api/numbers-search";
  import { getNotificationsContext } from "svelte-notifications";
  import { defaultNotificationParams } from "@/lib/constants/notifications";

  export let theme: "phone-numbers" | "default";
  export let preselectedAreaCode: string;

  const themeConfigs = {
    default: {
      attributes: {
        areaCodeLabel: "Area Code",
      },
      classes: {
        areaCodeInput: "grow",
        formContainer: "flex align-end gap-10",
      },
      flags: {
        isPhoneInputShowed: false,
      },
    },
    ["phone-numbers"]: {
      attributes: {
        areaCodeLabel: "",
      },
      classes: {
        areaCodeInput: "area-code--phone-numbers",
        formContainer: "form-inputs--phone-numbers",
      },
      flags: {
        isPhoneInputShowed: true,
      },
    },
  };

  const themeConfig = themeConfigs[theme] || themeConfigs.default;

  const { addNotification } = getNotificationsContext();
  const errorMessage =
    "There was a problem or no numbers were returned in this area code.";

  // const notFoundMessage = "No numbers found in this area code.";

  let result: number[] = [];
  let areaCodeResult: number[] = [];

  const searchNumberMutation = createMutation<void, Error, Payload>({
    mutationFn: async (data) => {
      result = [];
      const searchResponse = await searchPhoneNumber(data);

      if (searchResponse.result === "error") {
        addNotification({
          ...defaultNotificationParams,
          text: errorMessage,
        });
        return;
      }

      // if (!searchResponse.numbers.length) {
      //   addNotification({
      //     ...defaultNotificationParams,
      //     type: "warning",
      //     text: notFoundMessage,
      //   });
      //   return;
      // }

      result = searchResponse.numbers || [];

      if (searchTerm) {
        result = (searchResponse.numbers || []).filter((num: string) => {
          const withoutAreacode = num.slice(3);
          return withoutAreacode.indexOf(textToNumbers(searchTerm)) !== -1;
        });
      }

      result = result.slice(0, 13);
    },
    onError: () => {
      addNotification({
        ...defaultNotificationParams,
        text: errorMessage,
      });
    },
  });

  const searchAreaCodeMutation = createMutation<void, Error, Payload>({
    mutationFn: async (data) => {
      areaCodeResult = [];
      const searchResponse = await searchPhoneNumber(data);

      if (searchResponse.result === "error") {
        addNotification({
          ...defaultNotificationParams,
          text: errorMessage,
        });
        return;
      }

      // if (!searchResponse.numbers.length) {
      //   addNotification({
      //     ...defaultNotificationParams,
      //     type: "warning",
      //     text: notFoundMessage,
      //   });
      //   return;
      // }

      areaCodeResult = searchResponse.numbers || [];
    },
    onError: () => {
      addNotification({
        ...defaultNotificationParams,
        text: errorMessage,
      });
    },
  });

  let areaCode = preselectedAreaCode;
  let areaCodeError = "";
  let phoneInputError = "";
  let searchTerm = "";
  let prevSearchTerm = "";

  const handleSearch = async () => {
    if (searchTerm) {
      if (searchTerm.length < 3) {
        phoneInputError = "Search term length must be at least 3 symbols";
      }
    }

    if (!areaCode) {
      areaCodeError = "Area code is required - Specify the area code";
    }

    if (areaCodeError || phoneInputError) {
      return;
    }

    prevSearchTerm = searchTerm;

    $searchNumberMutation.mutate({
      action: "searchareacode",
      areacode: areaCode,
      maxresults: 500,
      pattern: searchTerm,
    });

    $searchAreaCodeMutation.mutate({
      action: "searchareacode",
      areacode: areaCode,
      maxresults: 12,
      pattern: "",
    });
  };

  const handleAreaCodeChange = (e: Event) => {
    const target = e.target as HTMLInputElement;

    if (!target) {
      return;
    }

    areaCodeError = "";
    areaCode = target.value.replace(/\D/g, "").slice(0, 3);
  };

  const handleSearchTermChange = () => {
    phoneInputError = "";
  };

  const handleNumberClick = (number: number) => {
    localStorage.setItem("reserved_vanity_number", number.toString());
    window.location.href = `https://www.dialmycalls.com/signup`;
  };
</script>

<div class="mt-20 w-full">
  <div class={themeConfig.classes.formContainer}>
    {#if !preselectedAreaCode}
      <div class={themeConfig.classes.areaCodeInput}>
        <TextInput
          type="text"
          error={areaCodeError
            ? phoneInputError
              ? `<span class="hide-on-desktop">${areaCodeError}</span>`
              : areaCodeError
            : ""}
          class={`${areaCodeError ? "form-input--error" : ""}`}
          label={themeConfig.attributes.areaCodeLabel}
          on:input={handleAreaCodeChange}
          bind:value={areaCode}
          placeholder="847"
        />
      </div>
    {/if}

    {#if themeConfig.flags.isPhoneInputShowed}
      <div class="grow">
        <PhoneInput
          type="text"
          label={themeConfig.attributes.areaCodeLabel}
          withLetters={true}
          error={phoneInputError
            ? areaCodeError
              ? `<span class="hide-on-desktop">${phoneInputError}</span>`
              : phoneInputError
            : ""}
          class={`${phoneInputError ? "form-input--error" : ""}`}
          on:value={handleSearchTermChange}
          bind:value={searchTerm}
          hideAreaCode={true}
          placeholder="Search by numbers or text (e.g., 678 or ZOOM)"
        />
      </div>
    {/if}

    <button
      type="button"
      on:click={handleSearch}
      disabled={$searchNumberMutation.isPending}
      class={cn("button", {
        button_loading: $searchNumberMutation.isPending,
      })}
    >
      Search
    </button>
  </div>

  {#if phoneInputError && areaCodeError}
    <div
      class="hide-on-mobile flex-col flex color-error whitespace-nowrap mt-15 gap-4"
    >
      <span>{areaCodeError}</span>
      <span>{phoneInputError}</span>
    </div>
  {/if}

  {#if $searchNumberMutation.isSuccess && result.length}
    <div class="flex flex-col align-center justify-center mt-40 mb-50">
      <div class="flex flex-col align-center gap-4">
        <span
          ><span class="font-xxl phone-number-available-number"
            >{formatPhoneNumber(result[0].toString(), prevSearchTerm)}</span
          > is available!</span
        >
        {#if hasLetters(prevSearchTerm)}
          <span class="text-12 color-gray">
            {formatUSPhoneNumber(result[0].toString())}
          </span>
        {/if}
      </div>
      <button
        class="button button_small mt-15 phone-number-pick-button-with-arrow"
        on:click={() => handleNumberClick(result[0])}
      >
        Reserve & Sign Up <IconArrowNarrowRight
          class="button__icon_right phone-number-pick-button-arrow"
        />
      </button>
    </div>

    {#if result.length > 1}
      <div class="mt-20 mb-10 font-medium text-center">
        Other Available Matches
      </div>
      <div class="available-numbers-container">
        {#each result.slice(1) as number}
          <button
            class={`button button_ghost font-normal ${hasLetters(prevSearchTerm)} ${hasLetters(prevSearchTerm) ? "phone-number--with-text" : ""}`}
            on:click={() => handleNumberClick(number)}
          >
            <span
              class={`font-normal ${hasLetters(prevSearchTerm)} ${hasLetters(prevSearchTerm) ? "font-l" : ""}`}
            >
              {formatPhoneNumber(number.toString(), prevSearchTerm)}
            </span>
            {#if hasLetters(prevSearchTerm)}
              <span class="font-s font-normal color-gray">
                {formatUSPhoneNumber(number.toString())}
              </span>
            {/if}
          </button>
        {/each}
      </div>
    {/if}
  {/if}

  {#if $searchNumberMutation.isSuccess && !result.length}
    <div class="mt-50 mb-30 text-center">
      No numbers found, try a different search term
    </div>
  {/if}

  {#if $searchAreaCodeMutation.isSuccess && areaCodeResult.length && result.length <= 1}
    <div class="mt-20 mb-10 font-medium text-center">
      Other Available Matches for Area Code {areaCode}
    </div>
    <div class="available-numbers-container">
      {#each areaCodeResult as number}
        <button
          class="button button_ghost"
          on:click={() => handleNumberClick(number)}
        >
          <span class="font-normal"
            >{formatUSPhoneNumber(number.toString())}</span
          >
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  .available-numbers-container {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
</style>
